import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tracking component to be included in App.js
export const AnalyticsTracker = () => {
  const location = useLocation();
  const startTime = useRef(Date.now());
  const sessionId = useRef(null);

  // Generate or retrieve session ID
  useEffect(() => {
    let sid = sessionStorage.getItem('analytics_session');
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('analytics_session', sid);
    }
    sessionId.current = sid;
  }, []);

  // Track page view on route change
  useEffect(() => {
    const trackPageView = async () => {
      try {
        // Get referrer info
        const referrer = document.referrer;
        let source = 'direct';
        
        if (referrer) {
          const refUrl = new URL(referrer);
          const refHost = refUrl.hostname.toLowerCase();
          
          if (refHost.includes('google')) source = 'Google';
          else if (refHost.includes('bing')) source = 'Bing';
          else if (refHost.includes('yahoo')) source = 'Yahoo';
          else if (refHost.includes('facebook') || refHost.includes('fb.com')) source = 'Facebook';
          else if (refHost.includes('instagram')) source = 'Instagram';
          else if (refHost.includes('twitter') || refHost.includes('x.com')) source = 'Twitter/X';
          else if (refHost.includes('linkedin')) source = 'LinkedIn';
          else if (refHost.includes('youtube')) source = 'YouTube';
          else if (refHost.includes('tiktok')) source = 'TikTok';
          else if (refHost.includes('pinterest')) source = 'Pinterest';
          else if (!refHost.includes(window.location.hostname)) source = refHost;
        }

        // Check for UTM parameters
        const urlParams = new URLSearchParams(window.location.search);
        const utmSource = urlParams.get('utm_source');
        const utmMedium = urlParams.get('utm_medium');
        const utmCampaign = urlParams.get('utm_campaign');
        
        if (utmSource) source = utmSource;

        // Detect device type
        const userAgent = navigator.userAgent.toLowerCase();
        let device = 'desktop';
        if (/mobile|android|iphone|ipod|blackberry|windows phone/i.test(userAgent)) {
          device = 'mobile';
        } else if (/ipad|tablet|playbook|silk/i.test(userAgent)) {
          device = 'tablet';
        }

        // Detect browser
        let browser = 'Unknown';
        if (userAgent.includes('firefox')) browser = 'Firefox';
        else if (userAgent.includes('edg')) browser = 'Edge';
        else if (userAgent.includes('chrome')) browser = 'Chrome';
        else if (userAgent.includes('safari')) browser = 'Safari';
        else if (userAgent.includes('opera') || userAgent.includes('opr')) browser = 'Opera';

        // Detect OS
        let os = 'Unknown';
        if (userAgent.includes('windows')) os = 'Windows';
        else if (userAgent.includes('mac')) os = 'macOS';
        else if (userAgent.includes('linux')) os = 'Linux';
        else if (userAgent.includes('android')) os = 'Android';
        else if (userAgent.includes('iphone') || userAgent.includes('ipad')) os = 'iOS';

        // Send tracking data
        await axios.post(`${API_URL}/api/analytics/track`, {
          session_id: sessionId.current,
          path: location.pathname,
          search: location.search,
          title: document.title,
          referrer: referrer,
          source: source,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
          device: device,
          browser: browser,
          os: os,
          screen_width: window.screen.width,
          screen_height: window.screen.height,
          language: navigator.language,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        });

        // Reset start time for new page
        startTime.current = Date.now();
      } catch (error) {
        // Silent fail - don't break the app if tracking fails
        console.debug('Analytics tracking error:', error);
      }
    };

    // Small delay to ensure page has loaded
    const timer = setTimeout(trackPageView, 100);
    return () => clearTimeout(timer);
  }, [location.pathname, location.search]);

  // Track time on page when leaving
  useEffect(() => {
    const trackTimeOnPage = () => {
      const timeOnPage = Math.round((Date.now() - startTime.current) / 1000);
      if (timeOnPage > 1) {
        // Use sendBeacon for reliable tracking on page unload
        const data = JSON.stringify({
          session_id: sessionId.current,
          path: location.pathname,
          time_on_page: timeOnPage
        });
        navigator.sendBeacon(`${API_URL}/api/analytics/time`, data);
      }
    };

    window.addEventListener('beforeunload', trackTimeOnPage);
    return () => window.removeEventListener('beforeunload', trackTimeOnPage);
  }, [location.pathname]);

  return null; // This component doesn't render anything
};

export default AnalyticsTracker;
